import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import PublicBrowseShell from '../../components/public/PublicBrowseShell';
import Icon from '../../components/AppIcon';
import Image from '../../components/AppImage';
import Button from '../../components/ui/Button';
import TeamRoster from '../team-profiles/components/TeamRoster';
import TeamStatistics from '../team-profiles/components/TeamStatistics';
import RecentMatches from '../team-profiles/components/RecentMatches';
import { regionalTeamService } from '../../services/regionalTeamService';
import { regionalTeamRosterService } from '../../services/regionalTeamRosterService';
import { tournamentService } from '../../services/tournamentService';
import { getRegionLabel } from '../../utils/tanzaniaRegions';

const TABS = [
  { id: 'overview', label: 'Overview', icon: 'Info' },
  { id: 'roster', label: 'Roster', icon: 'Users' },
  { id: 'statistics', label: 'Statistics', icon: 'BarChart3' },
  { id: 'matches', label: 'Matches', icon: 'Calendar' },
];

const InfoRow = ({ icon, label, value }) => (
  <div className="flex items-start gap-3">
    <Icon name={icon} size={16} className="text-muted-foreground mt-0.5 shrink-0" />
    <div className="min-w-0">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="text-foreground font-medium break-words">{value || 'N/A'}</p>
    </div>
  </div>
);

const statusBadgeClass = (status) => {
  if (status === 'active' || status === 'approved') return 'bg-success/10 text-success';
  if (status === 'pending_approval' || status === 'pending') return 'bg-warning/10 text-warning';
  return 'bg-muted text-muted-foreground';
};

const formatShortDate = (value) => {
  if (!value) return 'N/A';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'N/A';
  return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' }).format(date);
};

const mapGameForTeam = (game, teamId) => {
  const isHome = game.homeTeamId === teamId;
  const opponent = isHome ? game.awayTeam : game.homeTeam;
  const teamScore = isHome ? game.homeScore : game.awayScore;
  const opponentScore = isHome ? game.awayScore : game.homeScore;
  let result = null;
  if (
    typeof teamScore === 'number' &&
    typeof opponentScore === 'number' &&
    (game.gameStatus === 'completed' || game.gameStatus === 'final')
  ) {
    if (teamScore > opponentScore) result = 'Win';
    else if (teamScore < opponentScore) result = 'Loss';
    else result = 'Tie';
  }

  return {
    id: game.id,
    scheduledDate: game.scheduledDate,
    venue: game.venue || 'TBD',
    matchStatus: game.gameStatus,
    isHome,
    opponent: { name: opponent?.teamName || 'Unknown Team', logoUrl: opponent?.logoUrl },
    teamScore,
    opponentScore,
    result,
  };
};

const buildPerformanceTrend = (completedMatches = []) => {
  if (!completedMatches.length) return [];
  const trendMap = new Map();
  completedMatches.forEach((match) => {
    if (!match?.scheduledDate) return;
    const monthKey = new Intl.DateTimeFormat('en-US', { month: 'short' }).format(
      new Date(match.scheduledDate)
    );
    const existing = trendMap.get(monthKey) || { month: monthKey, wins: 0, losses: 0 };
    if (match.result === 'Win') existing.wins += 1;
    else if (match.result === 'Loss') existing.losses += 1;
    trendMap.set(monthKey, existing);
  });
  return Array.from(trendMap.values()).slice(-6);
};

const buildScoringTrend = (completedMatches = []) => {
  if (!completedMatches.length) return [];
  const recent = completedMatches.slice(0, 8).reverse();
  return recent.map((match, index) => ({
    game: match?.scheduledDate ? formatShortDate(match.scheduledDate) : `Game ${recent.length - index}`,
    scored: match?.teamScore ?? 0,
    allowed: match?.opponentScore ?? 0,
  }));
};

const RegionalTeamDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const fromRegionId = searchParams.get('from');

  const [team, setTeam] = useState(null);
  const [players, setPlayers] = useState([]);
  const [officials, setOfficials] = useState([]);
  const [matches, setMatches] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'overview');

  useEffect(() => {
    const tabFromUrl = searchParams.get('tab');
    if (tabFromUrl && TABS.some((t) => t.id === tabFromUrl)) {
      setActiveTab(tabFromUrl);
    }
  }, [searchParams]);

  useEffect(() => {
    loadTeam();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadTeam = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await regionalTeamService.getById(id);
      setTeam(data);
      document.title = `${data?.teamName || 'Regional Team'} - Tanzania Basketball Federation`;

      const [teamPlayers, teamOfficials, games] = await Promise.all([
        regionalTeamRosterService.getPlayers(id).catch(() => []),
        regionalTeamRosterService.getOfficials(id).catch(() => []),
        tournamentService.getGamesByTeam(id).catch(() => []),
      ]);

      setPlayers(teamPlayers || []);
      setOfficials(teamOfficials || []);
      setMatches((games || []).map((game) => mapGameForTeam(game, id)));
    } catch (err) {
      setError(err.message || 'Failed to load regional team');
    } finally {
      setIsLoading(false);
    }
  };

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    const next = new URLSearchParams(searchParams);
    next.set('tab', tabId);
    setSearchParams(next, { replace: true });
  };

  const handleBack = () => {
    if (fromRegionId) navigate(`/regions/${fromRegionId}`);
    else navigate('/regions');
  };

  const mappedRoster = useMemo(
    () =>
      (players || []).map((player) => ({
        id: player.id,
        name: player.fullName || 'Unknown Player',
        position: player.position || 'other',
        jerseyNumber: player.jerseyNumber,
        photoUrl: player.photoUrl || null,
        photoPath: player.photoPath || null,
        playerStatus: 'active',
        status: 'active',
        heightCm: null,
        weightKg: null,
        age: player.age,
        stats: {},
      })),
    [players]
  );

  const getPlayerProfileHref = useCallback(
    (player) => {
      if (!player?.id) return null;
      const params = new URLSearchParams();
      params.set('team', id);
      if (fromRegionId) params.set('from', fromRegionId);
      return `/regional-players/${player.id}?${params.toString()}`;
    },
    [id, fromRegionId]
  );

  const mappedMatches = useMemo(
    () =>
      (matches || []).map((match) => ({
        id: match.id,
        opponent: match.opponent?.name || 'Unknown Team',
        date: match.scheduledDate,
        venue: match.venue || 'TBD',
        isHome: match.isHome,
        teamScore: match.teamScore ?? 0,
        opponentScore: match.opponentScore ?? 0,
        result: match.result,
        highlights: [],
      })),
    [matches]
  );

  const mappedStatistics = useMemo(() => {
    if (!team) return null;
    const now = new Date();
    const completedMatches = (matches || []).filter((match) => {
      const hasScores =
        typeof match?.teamScore === 'number' && typeof match?.opponentScore === 'number';
      const matchDate = match?.scheduledDate ? new Date(match.scheduledDate) : null;
      const matchDayHasPassed = matchDate ? matchDate < now : false;
      const isCompleted =
        match?.matchStatus === 'completed' || match?.matchStatus === 'final';
      return hasScores && (matchDayHasPassed || isCompleted);
    });

    if (!completedMatches.length) return null;

    const wins = completedMatches.filter((m) => m.result === 'Win').length;
    const losses = completedMatches.filter((m) => m.result === 'Loss').length;
    const ties = completedMatches.filter((m) => m.result === 'Tie').length;
    const totalGames = completedMatches.length;
    const pointsFor = completedMatches.reduce((sum, m) => sum + (m.teamScore || 0), 0);
    const pointsAgainst = completedMatches.reduce((sum, m) => sum + (m.opponentScore || 0), 0);

    return {
      gamesPlayed: totalGames,
      wins,
      losses,
      ties,
      winPercentage: Number(((wins / totalGames) * 100).toFixed(1)),
      pointsPerGame: Number((pointsFor / totalGames).toFixed(1)),
      pointsAllowedPerGame: Number((pointsAgainst / totalGames).toFixed(1)),
      pointDifference: pointsFor - pointsAgainst,
      pointsFor,
      pointsAgainst,
      leaguePosition: null,
      performanceTrend: buildPerformanceTrend(completedMatches),
      scoringTrend: buildScoringTrend(completedMatches),
      homeRecord: {
        wins: completedMatches.filter((m) => m.isHome && m.result === 'Win').length,
        losses: completedMatches.filter((m) => m.isHome && m.result === 'Loss').length,
      },
      awayRecord: {
        wins: completedMatches.filter((m) => !m.isHome && m.result === 'Win').length,
        losses: completedMatches.filter((m) => !m.isHome && m.result === 'Loss').length,
      },
      recentMatches: completedMatches.slice(0, 5).map((match) => ({
        id: match.id,
        opponent: match.opponent?.name || 'Unknown Team',
        date: formatShortDate(match.scheduledDate),
        result: match.result || 'N/A',
        score: `${match.teamScore ?? 0} - ${match.opponentScore ?? 0}`,
      })),
    };
  }, [matches, team]);

  return (
    <PublicBrowseShell>
      <Button variant="ghost" size="sm" iconName="ArrowLeft" onClick={handleBack} className="mb-6">
        {fromRegionId ? 'Back to Region' : 'Back to Regions'}
      </Button>

      {isLoading ? (
        <div className="bg-card rounded-lg border border-border p-12 card-shadow text-center">
          <Icon name="Loader2" size={40} className="text-primary mx-auto mb-4 animate-spin" />
          <p className="text-muted-foreground">Loading regional team...</p>
        </div>
      ) : error ? (
        <div className="bg-destructive/10 border border-destructive/20 text-destructive rounded-lg p-6">
          <div className="flex items-start gap-3">
            <Icon name="AlertTriangle" size={20} />
            <p className="font-medium">{error}</p>
          </div>
        </div>
      ) : !team ? (
        <div className="bg-card rounded-lg border border-border p-12 card-shadow text-center">
          <Icon name="Shield" size={40} className="text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">Regional team not found.</p>
        </div>
      ) : (
        <div className="space-y-6">
          <div className="bg-card rounded-lg border border-border card-shadow p-6 flex items-center gap-6 flex-wrap">
            <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-lg bg-teal-600/10 flex items-center justify-center overflow-hidden shrink-0 border border-border">
              {team.logoUrl ? (
                <Image src={team.logoUrl} alt={team.teamName} className="w-full h-full object-contain p-1.5" />
              ) : (
                <Icon name="Shield" size={36} className="text-teal-600" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <h1 className="text-2xl font-bold text-foreground">{team.teamName || 'Unnamed Team'}</h1>
              <p className="text-muted-foreground">{getRegionLabel(team.region)} Regional Team</p>
              <div className="flex items-center gap-2 mt-2 flex-wrap">
                <span
                  className={`text-xs px-3 py-1 rounded-full capitalize ${statusBadgeClass(
                    team.registrationStatus
                  )}`}
                >
                  {(team.registrationStatus || 'pending').replace(/_/g, ' ')}
                </span>
                <span className="text-xs px-3 py-1 rounded-full bg-primary/10 text-primary">
                  {players.length} player{players.length === 1 ? '' : 's'}
                </span>
                {mappedStatistics && (
                  <span className="text-xs px-3 py-1 rounded-full bg-accent/10 text-accent-foreground">
                    {mappedStatistics.wins}-{mappedStatistics.losses}
                    {mappedStatistics.ties > 0 ? `-${mappedStatistics.ties}` : ''} record
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="bg-card rounded-lg border border-border card-shadow overflow-x-auto">
            <div className="flex min-w-max">
              {TABS.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => handleTabChange(tab.id)}
                  className={`flex items-center gap-2 px-4 py-3 text-sm font-medium whitespace-nowrap transition-colors border-b-2 ${
                    activeTab === tab.id
                      ? 'border-primary text-primary'
                      : 'border-transparent text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <Icon name={tab.icon} size={16} />
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          {activeTab === 'overview' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-card rounded-lg border border-border card-shadow p-6">
                <h2 className="text-lg font-semibold text-foreground mb-4">Team Information</h2>
                <div className="space-y-4">
                  <InfoRow icon="MapPin" label="Region" value={getRegionLabel(team.region)} />
                  <InfoRow
                    icon="BadgeCheck"
                    label="Registration Status"
                    value={(team.registrationStatus || 'pending').replace(/_/g, ' ')}
                  />
                  <InfoRow icon="Users" label="Roster Size" value={`${players.length} players`} />
                  <InfoRow icon="UserCog" label="Officials" value={`${officials.length} registered`} />
                </div>
              </div>

              <div className="bg-card rounded-lg border border-border card-shadow p-6">
                <h2 className="text-lg font-semibold text-foreground mb-4">Coaching & Staff</h2>
                <div className="space-y-4">
                  <InfoRow icon="Briefcase" label="Team Manager" value={team.teamManager} />
                  <InfoRow icon="UserCheck" label="Head Coach" value={team.headCoach} />
                  <InfoRow icon="Heart" label="Team Doctor" value={team.teamDoctor} />
                  <InfoRow icon="ClipboardList" label="Team Statistician" value={team.teamStatistician} />
                </div>

                {officials.length > 0 && (
                  <div className="mt-6 pt-4 border-t border-border">
                    <h3 className="text-sm font-semibold text-foreground mb-3">Additional Officials</h3>
                    <div className="space-y-2">
                      {officials.map((official) => (
                        <div
                          key={official.id}
                          className="flex justify-between gap-3 text-sm border border-border rounded-lg px-3 py-2"
                        >
                          <span className="font-medium text-foreground">{official.fullName}</span>
                          <span className="text-muted-foreground">{official.roleTitle}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'roster' &&
            (mappedRoster.length > 0 ? (
              <TeamRoster players={mappedRoster} getProfileHref={getPlayerProfileHref} />
            ) : (
              <div className="bg-card rounded-lg border border-border card-shadow p-10 text-center text-muted-foreground">
                <Icon name="Users" size={36} className="mx-auto mb-3 opacity-50" />
                <p>No players registered for this team yet.</p>
              </div>
            ))}

          {activeTab === 'statistics' && <TeamStatistics statistics={mappedStatistics} />}

          {activeTab === 'matches' &&
            (mappedMatches.length > 0 ? (
              <RecentMatches matches={mappedMatches} />
            ) : (
              <div className="bg-card rounded-lg border border-border card-shadow p-10 text-center text-muted-foreground">
                <Icon name="Calendar" size={36} className="mx-auto mb-3 opacity-50" />
                <p>No tournament matches found for this team yet.</p>
              </div>
            ))}
        </div>
      )}
    </PublicBrowseShell>
  );
};

export default RegionalTeamDetailPage;
